import { useEffect, useState } from 'react';

/**
 * Screenshot & print protection for sensitive offer documents.
 *
 * Covers:
 * - PrintScreen key → blurs content + clears clipboard
 * - Ctrl+P / Cmd+P (print) → blocked
 * - Ctrl+S (save) → blocked
 * - F12 (DevTools) → blocked
 * - Right-click → disabled
 * - CSS @media print → content hidden (strongest deterrent)
 * - Tab visibility change → blurs content
 *
 * Cannot prevent: OS snipping tools, phone camera, screen recording software.
 * These are impossible to block in any browser-based app — watermarking handles those.
 */
export function useScreenshotProtection() {
  const [isBlurred, setIsBlurred] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  useEffect(() => {
    // Inject CSS to hide content on print
    const style = document.createElement('style');
    style.id = 'screenshot-protection';
    style.textContent = `
      @media print {
        body * { visibility: hidden !important; }
        body::after {
          content: "Printing of this document is not permitted.";
          visibility: visible !important;
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 24px;
          font-weight: bold;
          color: #333;
        }
      }
    `;
    document.head.appendChild(style);

    const showWarning = (msg) => {
      setWarningMessage(msg);
      setIsBlurred(true);
      setTimeout(() => {
        setIsBlurred(false);
        setWarningMessage('');
      }, 3000);
    };

    const handleKeyDown = (e) => {
      // PrintScreen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        navigator.clipboard?.writeText('').catch(() => {});
        showWarning('Screenshots are not permitted for this document.');
      }
      // Ctrl+P / Cmd+P — print
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        showWarning('Printing is not permitted for this document.');
      }
      // Ctrl+S — save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
      }
      // F12 / Ctrl+Shift+I — DevTools allowed
      // Ctrl+U — view source
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
      }
    };

    // Blur when tab is hidden (screen recording / alt-tab heuristic)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsBlurred(true);
      } else {
        setTimeout(() => setIsBlurred(false), 500);
      }
    };

    // Block right-click globally
    const handleContextMenu = (e) => e.preventDefault();

    // Block beforeprint event
    const handleBeforePrint = (e) => {
      e.preventDefault();
      showWarning('Printing is not permitted for this document.');
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('contextmenu', handleContextMenu);
    window.addEventListener('beforeprint', handleBeforePrint);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      window.removeEventListener('beforeprint', handleBeforePrint);
      document.getElementById('screenshot-protection')?.remove();
    };
  }, []);

  return { isBlurred, warningMessage };
}
